import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Wrench, ArrowRight } from 'lucide-react';

const ToolsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Back button */}
      <div className="border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-red-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">

          {/* Page Header */}
          <div className="mb-12 text-center">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-red-500/10 border-2 border-red-500 rounded-xl flex items-center justify-center">
                <Wrench className="w-10 h-10 text-red-500" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-100 mb-4">
              The Operator's Toolkit
            </h1>
            <p className="text-xl text-gray-400 mb-6">
              Simulated & Safe
            </p>
            <p className="text-gray-300 max-w-3xl mx-auto leading-relaxed">
              These tools are theatrical props in the grand performance of datacenter management.
              They're client-side, non-functional in any real-world system, and designed purely
              for entertainment and satire.
            </p>
            <p className="text-gray-400 mt-4">
              Think of them as The Operator's toys: harmless, hilarious, and surprisingly cathartic.
            </p>
          </div>

          {/* Ethics Reminder Banner */}
          <div className="bg-amber-950/50 border border-amber-600 rounded-lg p-6 mb-12">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-amber-600/20 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">⚠️</span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-amber-400 mb-2">
                  These Tools Are Fictional
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  All tools on this page are simulated, safe, and run entirely in your browser.
                  They do not interact with real systems, networks, or data. They're satire, not software.
                </p>
              </div>
            </div>
          </div>

          {/* Featured Tools Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">

            {/* Tool 1: Excuse Generator */}
            <Link
              to="/tools/excuse-generator"
              className="bg-gray-900 border border-gray-800 rounded-lg p-8 hover:border-red-500 transition-all hover:shadow-lg hover:shadow-red-500/20 group"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 bg-red-500/10 border border-red-500/50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <img src="/tool-icon-excuse-generator.png" alt="" className="w-12 h-12 object-contain" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-100 mb-2 group-hover:text-red-500 transition-colors">
                    Excuse Generator
                  </h3>
                  <span className="inline-block px-2 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">
                    READY TO USE
                  </span>
                </div>
              </div>
              <p className="text-gray-400 leading-relaxed mb-4">
                Generate plausible-sounding explanations for any system failure. Powered by random
                combinations of technical jargon and creative blame assignment.
              </p>
              <div className="text-sm text-gray-500 mb-4">
                <strong className="text-gray-400">Features:</strong>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>50+ base excuses</li>
                  <li>Customizable severity levels</li>
                  <li>Export to clipboard</li>
                  <li>"Blame the PFY" mode</li>
                </ul>
              </div>
              <div className="inline-flex items-center gap-2 text-red-500 font-medium group-hover:gap-3 transition-all">
                Launch Tool
                <ArrowRight className="w-4 h-4" />
              </div>
            </Link>

            {/* Tool 2: Operator Response Simulator */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 opacity-75">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 bg-gray-800 border border-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                  <img src="/tool-icon-operator-simulator.png" alt="" className="w-12 h-12 object-contain opacity-50" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-100 mb-2">
                    Operator Response Simulator
                  </h3>
                  <span className="inline-block px-2 py-1 bg-gray-700 text-gray-400 text-xs font-bold rounded-full">
                    COMING SOON
                  </span>
                </div>
              </div>
              <p className="text-gray-400 leading-relaxed mb-4">
                Submit a fake user problem and receive The Operator's theatrical response.
                Watch as your "urgent" password reset gets the treatment it deserves.
              </p>
              <div className="text-sm text-gray-500 mb-4">
                <strong className="text-gray-400">Planned Features:</strong>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Personality slider (Helpful ↔ Sardonic)</li>
                  <li>Ticket category detection</li>
                  <li>PFY delegation probability</li>
                  <li>Response time: instant (unlike real IT)</li>
                </ul>
              </div>
            </div>

            {/* Tool 3: Buzzword Salad Generator */}
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 opacity-75">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 bg-gray-800 border border-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                  <img src="/tool-icon-buzzword-salad.png" alt="" className="w-12 h-12 object-contain opacity-50" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-100 mb-2">
                    Buzzword Salad Generator
                  </h3>
                  <span className="inline-block px-2 py-1 bg-gray-700 text-gray-400 text-xs font-bold rounded-full">
                    COMING SOON
                  </span>
                </div>
              </div>
              <p className="text-gray-400 leading-relaxed mb-4">
                Create meaningless corporate-speak at scale. Perfect for emails, incident reports,
                and meetings where you're trying to say nothing eloquently.
              </p>
              <div className="text-sm text-gray-500 mb-4">
                <strong className="text-gray-400">Planned Features:</strong>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Email templates</li>
                  <li>Incident report mode</li>
                  <li>"Synergy" density slider</li>
                  <li>Export to corporate memo format</li>
                </ul>
              </div>
            </div>

            {/* Tool 4: The Clipboard */}
            <Link
              to="/tools/clipboard"
              className="bg-gray-900 border border-gray-800 rounded-lg p-8 hover:border-teal-500 transition-all hover:shadow-lg hover:shadow-teal-500/20 group"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 bg-teal-500/10 border border-teal-500/50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <img src="/tool-icon-clipboard.png" alt="" className="w-12 h-12 object-contain" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-100 mb-2 group-hover:text-teal-500 transition-colors">
                    The Clipboard
                  </h3>
                  <span className="inline-block px-2 py-1 bg-teal-500/20 text-teal-400 text-xs font-bold rounded-full">
                    READY TO USE
                  </span>
                </div>
              </div>
              <p className="text-gray-400 leading-relaxed mb-4">
                The Operator's personal notes: random observations, sysadmin wisdom, and sarcastic tips.
                Every interaction documented. Every incident archived.
              </p>
              <div className="text-sm text-gray-500 mb-4">
                <strong className="text-gray-400">Features:</strong>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>90+ observations</li>
                  <li>4 categories (Users, PFY, Management, Technical)</li>
                  <li>Share individual notes</li>
                  <li>Daily wisdom feature</li>
                </ul>
              </div>
              <div className="inline-flex items-center gap-2 text-teal-500 font-medium group-hover:gap-3 transition-all">
                Launch Tool
                <ArrowRight className="w-4 h-4" />
              </div>
            </Link>

          </div>

          {/* Ethics Banner */}
          <div className="ethics-banner">
            <div className="text-center">
              <p className="text-sm text-gray-300 leading-relaxed">
                ⚠️ All content on AI Operator From Hell is <strong>fictional and satirical</strong>.
                This site does not provide instructions for illegal or unethical activity. For real-world learning, visit{' '}
                <a href="https://owasp.org" target="_blank" rel="noopener noreferrer">OWASP</a>,{' '}
                <a href="https://sans.org" target="_blank" rel="noopener noreferrer">SANS</a>, and{' '}
                <a href="https://nist.gov/cyberframework" target="_blank" rel="noopener noreferrer">NIST</a>.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Link to="/" className="btn btn-secondary">
              Back to Episodes
            </Link>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ToolsPage;
